import { Injectable } from '@angular/core';
import { HttpEvent } from '@angular/common/http';

import { DateTime } from 'luxon';
import { of, from } from 'rxjs';
import { switchMap, map, tap, catchError, withLatestFrom } from 'rxjs/operators';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Update } from '@ngrx/entity';

import { UsersMiscDomainService } from '@apps/auth/domain/users-misc-domain.service';
import { User } from '@apps/auth/domain/models/back/user';
import { Profile } from '@apps/auth/models/profile';
import { NotificationService, NotifType, NotfGravity, MediaTypes, FilesService, DatesService } from '@common/services';
import * as fromBackAuthUsersGlobalActions from '@apps/back/store/auth/users/global/users-global.actions';
import { BackAuthExternalUserHabsUpdated, BackAuthUsersDeactivateExternalUserSucceeded, BackAuthUsersActivateExternalUserSucceeded, BackAuthUsersExternalIdentityUpdated, BackAuthUsersExternalUserUpdated } from '@apps/back/store/auth/users/external/users-external.actions';
import { BackAuthInternalUserHabsUpdated, BackAuthUsersDeactivateInternalUserSucceeded, BackAuthUsersActivateInternalUserSucceeded, BackAuthUsersInternalIdentityUpdated } from '@apps/back/store/auth/users/internal/users-internal.actions';
import { BackAuthEntityUserSelectedSet } from '@apps/back/store/auth/entities/global/entities-global.actions';
import * as fromAuthStore from '@apps/auth/store';
import { IAuroraState } from '@apps/aurora.state';
import { BackAuthUsersHabsUpdateFailed } from '@apps/back/store/auth/users/global/users-global.actions';
import { BackAuthEntityExternalUpdated } from '../../entities/external/entities-external.actions';
import { Entity } from '@apps/auth/domain/models/back';
import { BackAuthEntityInternalUpdated } from '../../entities/internal/entities-internal.actions';
import { AuthenticationService } from '@apps/auth/api/authentication.service';
import { CreateUserViewModel } from '@apps/back/models/create-user-view-model';

@Injectable()
export class BackAuthUsersGlobalEffects {
    constructor(
        private actions: Actions,
        private store: Store<IAuroraState>,
        private usersMiscDomainService: UsersMiscDomainService,
        private notificationService: NotificationService,
        private filesService: FilesService,
        private datesService: DatesService,
        private authenticationService: AuthenticationService
    ) { }

    backAuthUsersIdentityUpdateRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersIdentityUpdateRequested),
                switchMap((action: any) =>
                    this.usersMiscDomainService.updateUser(action.user)
                        .pipe(
                            map((user: User) =>
                                fromBackAuthUsersGlobalActions.BackAuthUsersIdentityUpdateSucceeded({ user })
                            ),
                            catchError(() =>
                                of(fromBackAuthUsersGlobalActions.BackAuthUsersIdentityUpdateFailed())
                            )
                        )
                )
            )
    );

    backAuthUsersIdentityUpdateSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersIdentityUpdateSucceeded),
                tap(() => {
                    this.notificationService.notify([
                        '',
                        'ADMIN.USERS.ACTIONS.EDITUSER.NOTF.PROFILE.SUCCEEDED'
                    ],
                        NotfGravity.success,
                        NotifType.SNACKBAR
                    );
                }),
                map(({ user }: { user: User }) => {
                    const updateUser: Update<User> = {
                        id: user.id,
                        changes: {
                            ...user
                        }
                    };

                    return user.external
                        ? BackAuthUsersExternalIdentityUpdated({ user: updateUser })
                        : BackAuthUsersInternalIdentityUpdated({ user: updateUser });
                }

                )
            )
    );

    backAuthUsersIdentityUpdateFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersIdentityUpdateFailed),
                tap(() => {
                    this.notificationService.notify([
                        '',
                        'ADMIN.USERS.ACTIONS.EDITUSER.NOTF.PROFILE.FAILED'
                    ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR
                    );
                })
            ),
        { dispatch: false }
    );

    backAuthUsersHabsUpdateRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersHabsUpdateRequested),
                switchMap((action: any) =>
                    this.usersMiscDomainService.replaceAllHabilitations(action.userId, action.habs)
                        .pipe(
                            switchMap((user: User) =>
                                [
                                    fromBackAuthUsersGlobalActions.BackAuthUsersHabsUpdateSucceeded({ user }),
                                    BackAuthEntityUserSelectedSet({ user })
                                ]
                            ),
                            catchError(() =>
                                of(BackAuthUsersHabsUpdateFailed())
                            )
                        )
                )
            )
    );

    backAuthUsersHabsUpdateSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersHabsUpdateSucceeded),
                tap(() => {
                    this.notificationService.notify([
                        '',
                        'ADMIN.USERS.ACTIONS.EDITUSER.NOTF.ROLES.SUCCEEDED'
                    ],
                        NotfGravity.success,
                        NotifType.SNACKBAR
                    );
                }),
                map(({ user }: { user: User }) => {
                    const updateUser: Update<User> = {
                        id: user.id,
                        changes: {
                            ...user
                        }
                    };

                    return user.external
                        ? BackAuthExternalUserHabsUpdated({ user: updateUser })
                        : BackAuthInternalUserHabsUpdated({ user: updateUser });
                })
            )
    );

    backAuthUsersHabsUpdateFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(BackAuthUsersHabsUpdateFailed),
                tap(() => {
                    this.notificationService.notify([
                        '',
                        'ADMIN.USERS.ACTIONS.EDITUSER.NOTF.ROLES.FAILED'
                    ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR
                    );
                })
            ),
        { dispatch: false }
    );

    backAuthUsersExportRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBackAuthUsersGlobalActions.BackAuthUsersExportRequested),
                    switchMap(({ qs, format }: { qs: string, format: MediaTypes }) =>
                        this.usersMiscDomainService.export(qs, format)
                            .pipe(
                                map((response) =>
                                    fromBackAuthUsersGlobalActions.BackAuthUsersExportSucceeded({ response })
                                ),
                                catchError((failure: any) =>
                                    of(fromBackAuthUsersGlobalActions.BackAuthUsersExportFailed(failure))
                                ))
                    )
                )
    );

    backAuthUsersExportSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBackAuthUsersGlobalActions.BackAuthUsersExportSucceeded),
                    withLatestFrom(this.store.select(fromAuthStore.getProfile)),
                    tap(([params, profile]: [{ response: HttpEvent<Blob>; }, Profile]) => {
                        const now = this.datesService.DateTimeToFull(DateTime.now()).replace(/[^0-9]/g, '');
                        this.filesService.saveFileFromApi(params.response, profile, `${profile.entityName}-users-${now}.csv`.toLowerCase());
                    })
                ),
        { dispatch: false }
    );

    backAuthUsersAddHabilitationRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersAddHabilitationRequested),
                switchMap(({ external, userId, appRole }) =>
                    this.usersMiscDomainService.addHabilitations(userId, [appRole])
                        .pipe(
                            switchMap((user: User) => {
                                const updateUser: Update<User> = {
                                    id: user.id,
                                    changes: {
                                        appRoles: user.appRoles
                                    }
                                };

                                return [
                                    fromBackAuthUsersGlobalActions.BackAuthUsersAddHabilitationSucceeded({ appRole }),
                                    BackAuthEntityUserSelectedSet({ user }),
                                    external ? BackAuthExternalUserHabsUpdated({ user: updateUser }) : BackAuthInternalUserHabsUpdated({ user: updateUser })
                                ];
                            }),
                            catchError(() =>
                                of(fromBackAuthUsersGlobalActions.BackAuthUsersAddHabilitationFailed)
                            )
                        )
                )
            )
    );

    backAuthUsersAddHabilitationSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersAddHabilitationSucceeded),
                tap(({ appRole }) => {
                    this.notificationService.notify(
                        [
                            '',
                            'ADMIN.USERS.ROLES.SUCCESS.ADDED'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR,
                        {
                            module: appRole.application
                        }
                    );
                })
            ),
        { dispatch: false }
    );

    backAuthUsersAddHabilitationFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersAddHabilitationFailed),
                tap(({ appRole }) => {
                    this.notificationService.notify(
                        [
                            '',
                            'ADMIN.USERS.ROLES.FAILED.ADDED'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR,
                        {
                            module: appRole.application
                        }
                    );
                })
            ),
        { dispatch: false }
    );

    backAuthUsersRemoveHabilitationRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersRemoveHabilitationRequested),
                switchMap(({ external, userId, appRole }) =>
                    this.usersMiscDomainService.removeHabilitations(userId, [appRole])
                        .pipe(
                            switchMap((user: User) => {
                                const updateUser: Update<User> = {
                                    id: user.id,
                                    changes: {
                                        appRoles: user.appRoles
                                    }
                                };

                                return [
                                    fromBackAuthUsersGlobalActions.BackAuthUsersRemoveHabilitationSucceeded({ appRole }),
                                    BackAuthEntityUserSelectedSet({ user }),
                                    external ? BackAuthExternalUserHabsUpdated({ user: updateUser }) : BackAuthInternalUserHabsUpdated({ user: updateUser })
                                ];
                            }),
                            catchError(() =>
                                of(fromBackAuthUsersGlobalActions.BackAuthUsersRemoveHabilitationFailed({ appRole }))
                            )
                        )
                )
            )
    );

    backAuthUsersRemoveHabilitationSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersRemoveHabilitationSucceeded),
                tap(({ appRole }) => {
                    this.notificationService.notify(
                        [
                            '',
                            'ADMIN.USERS.ROLES.SUCCESS.REMOVED'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR,
                        {
                            module: appRole.application
                        }
                    );
                })
            ),
        { dispatch: false }
    );

    backAuthUsersRemoveHabilitationFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersRemoveHabilitationFailed),
                tap(({ appRole }) => {
                    this.notificationService.notify(
                        [
                            '',
                            'ADMIN.USERS.ROLES.FAILED.REMOVED'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR,
                        {
                            module: appRole.application
                        }
                    );
                })
            ),
        { dispatch: false }
    );

    backAuthUsersSetIsServiceRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersSetIsServiceRequested),
                switchMap(({ external, userId, isService }) =>
                    this.usersMiscDomainService.setIsService(userId, isService)
                        .pipe(
                            switchMap((user: User) => {
                                const updateUser: Update<User> = {
                                    id: user.id,
                                    changes: {
                                        isServiceAccount: user.isServiceAccount
                                    }
                                };

                                return [
                                    fromBackAuthUsersGlobalActions.BackAuthUsersSetIsServiceSucceeded({ isService }),
                                    BackAuthEntityUserSelectedSet({ user }),
                                    external
                                        ? BackAuthUsersExternalIdentityUpdated({ user: updateUser })
                                        : BackAuthUsersInternalIdentityUpdated({ user: updateUser })
                                ];
                            }),
                            catchError(() =>
                                of(fromBackAuthUsersGlobalActions.BackAuthUsersSetIsServiceFailed({ isService }))
                            )
                        )
                )
            )
    );

    backAuthUsersSetIsServiceSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersSetIsServiceSucceeded),
                tap(({ isService }) =>
                    this.notificationService.notify(
                        [
                            '',
                            isService ? 'Compte marqué comme service' : 'Compte démarqué comme service'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR
                    )
                )
            ),
        { dispatch: false }
    );

    backAuthUsersSetIsServiceFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersSetIsServiceFailed),
                tap(({ isService }) =>
                    this.notificationService.notify(
                        [
                            '',
                            isService ? 'Impossible de marquer le compte comme service' : 'Impossible de démarquer le compte comme service'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR
                    )
                )
            ),
        { dispatch: false }
    );

    // Interpret AD check result; decide if creation should proceed and provide
    // a human-readable message when it shouldn't.
    private resolveAdCheckOutcome(adCheckResult: any, entity: Entity, user: CreateUserViewModel) {
        const email = user?.email || 'cet utilisateur';

        if (typeof adCheckResult === 'string' && adCheckResult.trim()) {
            const text = adCheckResult.trim();
            const low = text.toLowerCase();

            if (low.includes('existe') || low.includes('déjà') || low.includes('présent')) {
                return { shouldCreate: false, notificationMessage: text, translationKey: '' };
            }

            return { shouldCreate: true, notificationMessage: '' };
        }

        const status = adCheckResult?.status || adCheckResult?.code || adCheckResult?.result;
        const entityName = adCheckResult?.entityName || adCheckResult?.entity?.name || adCheckResult?.entityNameFromAd;

        if (!status || status === 'ALLOWED' || status === 'CREATION_ALLOWED' || status === 'OK' || status === 'NOT_FOUND' || status === 'NOT_PRESENT') {
            return {
                shouldCreate: true,
                notificationMessage: ''
            };
        }

        if (status === 'ALREADY_EXISTS_IN_OTHER_ENTITY' || status === 'ALREADY_PRESENT_IN_OTHER_ENTITY') {
            return {
                shouldCreate: false,
                notificationMessage: `L'utilisateur ${email} existe déjà dans l'annuaire d'une autre entité${entityName ? ` (${entityName})` : ''}.`,
                translationKey: 'ADMIN.USERS.ACTIONS.CREATEUSER.NOTF.ALREADY_OTHER_ENTITY'
            };
        }

        if (status === 'ALREADY_EXISTS_IN_CURRENT_ENTITY' || status === 'ALREADY_PRESENT_IN_CURRENT_ENTITY' || status === 'ALREADY_EXISTS' || status === 'ALREADY_PRESENT') {
            return {
                shouldCreate: false,
                notificationMessage: `L'utilisateur ${email} est déjà présent dans l'annuaire de l'entité ${entity.name}.`,
                translationKey: 'ADMIN.USERS.ACTIONS.CREATEUSER.NOTF.ALREADY_CURRENT_ENTITY'
            };
        }

        return {
            shouldCreate: true,
            notificationMessage: '',
            translationKey: ''
        };
    }

    backAuthUsersAddUserRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersAddUserRequested),
                switchMap(({ entity, user }) => {
                    return this.usersMiscDomainService.checkUserInAd(user)
                        .pipe(
                            switchMap((adCheckResult: any) => {
                                const adCheckOutcome = this.resolveAdCheckOutcome(adCheckResult, entity, user);

                                if (!adCheckOutcome.shouldCreate) {
                                    return of(fromBackAuthUsersGlobalActions.BackAuthUsersAddUserFailed({
                                        message: adCheckOutcome.notificationMessage,
                                        translationKey: adCheckOutcome.translationKey
                                    }));
                                }

                                return this.usersMiscDomainService.createUser(user)
                                    .pipe(
                                        switchMap((newUser: User) => {
                                            const update: Update<Entity> = {
                                                id: entity.id,
                                                changes: {
                                                    nbUsers: entity.nbUsers + 1
                                                }
                                            };

                                            return [
                                                fromBackAuthUsersGlobalActions.BackAuthUsersAddUserSucceeded({ user: newUser }),
                                                entity.external ? BackAuthEntityExternalUpdated({ entity: update }) : BackAuthEntityInternalUpdated({ entity: update })
                                            ];
                                        }),
                                        catchError((error) => {
                                            const text = (typeof error?.error === 'string' && error.error.trim()) ? error.error.trim() : (error?.error?.message || error?.message || error?.statusText || null);
                                            return of(fromBackAuthUsersGlobalActions.BackAuthUsersAddUserFailed({ message: text, user }));
                                        })
                                    );
                            }),
                            catchError((error) => {
                                // If the AD check returned a 4xx with a payload describing
                                // the AD status, process that payload instead of treating
                                // it as a generic HTTP failure. This prevents "Http
                                // failure response" notifications when the backend is
                                // intentionally indicating existence in AD.
                                const adPayload = error && error.error && typeof error.error === 'object' ? error.error : null;

                                if (adPayload && (adPayload.status || adPayload.code || adPayload.result || adPayload.entityName || adPayload.entity)) {
                                    const adCheckOutcome = this.resolveAdCheckOutcome(adPayload, entity, user);

                                    if (!adCheckOutcome.shouldCreate) {
                                        return of(fromBackAuthUsersGlobalActions.BackAuthUsersAddUserFailed({
                                            message: adCheckOutcome.notificationMessage,
                                            translationKey: adCheckOutcome.translationKey
                                        }));
                                    }

                                    // If AD indicates creation is allowed, proceed to create
                                    // the user as usual.
                                    return this.usersMiscDomainService.createUser(user)
                                        .pipe(
                                            switchMap((newUser: User) => {
                                                const update: Update<Entity> = {
                                                    id: entity.id,
                                                    changes: {
                                                        nbUsers: entity.nbUsers + 1
                                                    }
                                                };

                                                return [
                                                    fromBackAuthUsersGlobalActions.BackAuthUsersAddUserSucceeded({ user: newUser }),
                                                    entity.external ? BackAuthEntityExternalUpdated({ entity: update }) : BackAuthEntityInternalUpdated({ entity: update })
                                                ];
                                            }),
                                            catchError((createError) => {
                                                return of(fromBackAuthUsersGlobalActions.BackAuthUsersAddUserFailed(createError));
                                            })
                                        );
                                }
                                // If AD check returns 405 with a clear existence message,
                                // use it as a business failure; otherwise continue and
                                // attempt the create POST so we capture the server's
                                // definitive error text.
                                if (error && error.status === 405) {
                                    const textual = (typeof error.error === 'string' && error.error.trim()) ? error.error.trim() : null;
                                    const low = textual ? textual.toLowerCase() : '';

                                    if (low.includes('existe') || low.includes('déjà') || low.includes('présent')) {
                                        return of(fromBackAuthUsersGlobalActions.BackAuthUsersAddUserFailed({ message: textual }));
                                    }
                                }

                                // No AD payload available, keep existing fallback: try creating the user.
                                return this.usersMiscDomainService.createUser(user)
                                    .pipe(
                                        switchMap((newUser: User) => {
                                            const update: Update<Entity> = {
                                                id: entity.id,
                                                changes: {
                                                    nbUsers: entity.nbUsers + 1
                                                }
                                            };

                                            return [
                                                fromBackAuthUsersGlobalActions.BackAuthUsersAddUserSucceeded({ user: newUser }),
                                                entity.external ? BackAuthEntityExternalUpdated({ entity: update }) : BackAuthEntityInternalUpdated({ entity: update })
                                            ];
                                        }),
                                        catchError((createError) => {
                                            // If the backend returned a Blob (e.g. text/plain), read it.
                                            if (createError && createError.error && typeof createError.error === 'object' && typeof (createError.error as any).text === 'function') {
                                                return from((createError.error as Blob).text()).pipe(
                                                    switchMap((txt: string) => of(fromBackAuthUsersGlobalActions.BackAuthUsersAddUserFailed({ message: txt && txt.trim() ? txt.trim() : null, user, raw: createError })))
                                                );
                                            }

                                            // Prefer server-provided plain text in `error`, else structured message.
                                            const serverText =
                                                (typeof createError?.error === 'string' && createError.error.trim())
                                                    ? createError.error.trim()
                                                    : (createError?.error && createError.error.message) || createError?.message || createError?.statusText || null;

                                            return of(fromBackAuthUsersGlobalActions.BackAuthUsersAddUserFailed({ message: serverText, user, raw: createError }));
                                        })
                                    );
                            })
                        );
                })
            )
    );

    backAuthUsersAddUserSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersAddUserSucceeded),
                tap(({ user }) => {

                    this.notificationService.notify([
                        'ADMIN.USERS.ACTIONS.CREATEUSER.NOTF.SUCCESS.TITLE',
                        'ADMIN.USERS.ACTIONS.CREATEUSER.NOTF.SUCCESS.MESSAGE'
                    ],
                        NotfGravity.success,
                        NotifType.SNACKBAR,
                        { email: user.email }
                    );
                })
            ),
        { dispatch: false }
    );

    backAuthUsersAddUserFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersAddUserFailed),
                tap((action) => {
                    const err: any = action || {};

                    // Normalize different shapes: the HttpErrorResponse may
                    // contain `error` as an object or as a plain string.
                    let payload: any;
                    if (err.error && typeof err.error === 'object') {
                        payload = err.error;
                    } else if (err.error && typeof err.error === 'string') {
                        // Preserve server plain-text message under a dedicated key
                        payload = { serverMessage: err.error };
                    } else {
                        payload = err;
                    }

                    const translationKey = payload.translationKey || payload.code || payload.status;
                    const rawErr = err && err.error;
                    const message = payload.serverMessage || payload.message || (typeof rawErr === 'string' ? rawErr : rawErr?.message) || payload.result || payload.errorMessage || err.message || err.statusText;

                    let rawText: string | null = null;
                    try {
                        if (payload && payload.raw) {
                            if (typeof payload.raw === 'string') rawText = payload.raw;
                            else if (payload.raw.error && typeof payload.raw.error === 'string') rawText = payload.raw.error;
                            else rawText = JSON.stringify(payload.raw);
                        }
                    } catch (e) {
                        rawText = null;
                    }

                    const notificationText = translationKey || message || rawText || 'ADMIN.USERS.ACTIONS.CREATEUSER.NOTF.ERROR.MESSAGE';

                    this.notificationService.notify([
                        'ADMIN.USERS.ACTIONS.CREATEUSER.NOTF.ERROR.TITLE',
                        notificationText
                    ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR,
                        { email: payload.user?.email || payload.userEmail || null, entityName: payload.entityName || null }
                    );
                })
            ),
        { dispatch: false }
    );

    backAuthUsersDeactivateUserRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersDeactivateUserRequested),
                switchMap((props) =>
                    this.usersMiscDomainService.deactivateUser(props.user.id)
                        .pipe(
                            map(() =>
                                props.user.external ?
                                    BackAuthUsersDeactivateExternalUserSucceeded({ userId: props.user.id }) : BackAuthUsersDeactivateInternalUserSucceeded({ userId: props.user.id })
                            )
                        )
                )
            )
    );

    backAuthUsersActivateUserRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersActivateUserRequested),
                switchMap((props) =>
                    this.usersMiscDomainService.activateUser(props.user.id)
                        .pipe(
                            map(() =>
                                props.user.external ?
                                    BackAuthUsersActivateExternalUserSucceeded({ userId: props.user.id }) : BackAuthUsersActivateInternalUserSucceeded({ userId: props.user.id })
                            )
                        )
                )
            )
    );

    backAuthUsersRelocateUserRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersRelocateUserRequested),
                switchMap(({ relocateViewModel }) =>
                    this.usersMiscDomainService.relocateUser(relocateViewModel.userId, relocateViewModel.entityId, relocateViewModel.entityName)
                        .pipe(
                            map((user) =>
                                fromBackAuthUsersGlobalActions.BackAuthUsersRelocateUserSucceeded(user)
                            ),
                            catchError(error =>
                                of(fromBackAuthUsersGlobalActions.BackAuthUsersRelocateUserFailed(error))
                            )
                        )
                )
            )
    );

    backAuthUsersRelocateUserSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersRelocateUserSucceeded),
                tap((user) => {
                    this.store.dispatch(BackAuthUsersExternalUserUpdated(user));

                    this.notificationService.notify(
                        [
                            '',
                            `Opération réussie. Nouvelle entité: ${user.entityName}`
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR
                    );
                })
            ),
        { dispatch: false }
    );

    backAuthUsersRelocateUserFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersRelocateUserFailed),
                tap(() =>
                    this.notificationService.notify(
                        ['', 'L\'opération a échouée'],
                        NotfGravity.danger,
                        NotifType.SNACKBAR
                    )
                )
            ),
        { dispatch: false }
    );

    backAuthUsersChangeUserPasswordRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersResetUserPasswordRequested),
                switchMap(({ email }) =>
                    this.authenticationService.resetUserPassword(email)
                        .pipe(
                            map(() =>
                                fromBackAuthUsersGlobalActions.BackAuthUsersResetUserPasswordSucceeded()
                            ),
                            catchError(error =>
                                of(fromBackAuthUsersGlobalActions.BackAuthUsersResetUserPasswordFailed(error))
                            )
                        )
                )
            )
    );

    backAuthUsersResetUserPasswordSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthUsersGlobalActions.BackAuthUsersResetUserPasswordSucceeded),
                tap((user) => {
                    this.notificationService.notify(
                        [
                            '',
                            'Le mot de passe a bien été modifié'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR
                    );
                })
            ),
        { dispatch: false }
    );
}
